<template>
  <base-dialog-button :isOpen="dialogOpen" @closeDialog="toggleDialog" :size="mobileVersion ? 'fs' : 'lg-fix-h'">
    <template slot="header">
      <div class="h-16 bg-secondary flex items-center justify-between text-white px-4">
        <div class="flex items-center">
          <h2 class="font-bold ml-2">{{ $t('invoices.dialogs.upload_verifactu_header') }}</h2>
        </div>
        <span class="cursor-pointer" @click="toggleDialog">
          <base-button-icon name="cross" size="xs" iconSize="sm" mode="secondary" />
        </span>
      </div>
    </template>
    <template slot="main">
      <div class="form-wrapper p-4 overflow-x-auto"
        :style="mobileVersion ? 'height: 80vh;' : 'height: 60vh;'">
        <el-form ref="ruleForm" class="h-full flex flex-col" :model="form" :rules="rules">
          <div class="lg:mb-2" v-if="[3, 8].includes(role)">
            <label
              class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon />{{ $t('clients.entity_name') }}</label>
            <el-form-item prop="ClienteID" class="mb-0">
              <base-el-select class="w-full" :options="clientes" v-model="form.ClienteID" filterable clearable @change="handleChangeClienteId" />
            </el-form-item>
          </div>
          <section>
            <div class="lg:flex files-container" v-if="form.ClienteID">
              <div class="main-files-container max-w-md">
                <div class="flex items-center mb-1">
                  <label class="text-sm font-semibold text-secondary-600 block"><base-obligatory-icon />
                  {{ $t('verifactu.verifactu_file') }}</label>
                  <base-tooltip-icon size="sm" :content="extensionTooltipVerifactuMessage" class="ml-2 " />
                </div>
                <div class="px-2">
                  <el-form-item prop="fileMain" ref="fileMainRef">
                    <div @dragenter="e => e.preventDefault()" @dragover="e => e.preventDefault()" @drop.prevent.stop="onFileMainDrop">
                      <Upload ref="uploadMain" id="fileMainSelector" class="bg-white dark:bg-darkblue-900"
                      :class="[isLower1024 ? 'custom-upload-cert' : 'custom-upload ']" :accept="verifactuAcceptFiles"
                      drag action="#" :auto-upload="false" :on-remove="handleRemoveMain" multiple :limit="pgeUploadLimitComputed"
                      :on-change="handleChangeMain" :on-exceed="handleOnExceedMain" :file-list="form.fileMain">
                      <i class="el-icon-upload"></i>
                      <p v-if="form.fileMain.length" slot="default" class="el-upload__text"><em>{{ this.fileNameMain }}</em></p>
                      <div v-else slot="default" class="el-upload__text" v-safe-html="$tc('app.upload_default_no_file_text', pgeUploadLimitComputed)"></div>
                      <upload-file-item slot="file" slot-scope="{file}" :file="file"
                        :hide-expand-action="file.validations && file.validations.some(v => v.type === ErrorType.DUPLICATED_FILE)" :deep="2" custom="!max-h-fit" @remove="handleRemoveMain" @click="selectFile" reverse-colors />
                      </Upload>
                    </div>
                  </el-form-item>
                </div>
              </div>
            </div>
          </section>
        </el-form>
      </div>
    </template>
    <template slot="footer">
      <div class="flex flex-row w-full justify-between">
        <base-button class="w-full mx-2" @click="toggleDialog" mode="secondary-phantom-blue" :text="$t('app.cancel')" />
        <base-button :disabled="disableSubmit" :loading="isLoadingUpdate" mode="secondary-phantom-blue" class="w-full mx-2"
        :text="$tc('invoices.dialogs.upload_invoice', 1)" @click="submitForm('ruleForm')" />
      </div>
    </template>
  </base-dialog-button>
</template>

<script>
import UploadDocumentMixin, { WarningType, ErrorType } from '@/mixins/UploadDocumentMixin'
import { getClients } from '@/services/clients/getClients'

import { altaVerifactuPortal } from '@/services/configurations/altaVerifactuPortal'

import { Upload } from 'element-ui'
import { i18n } from '@/i18n'
import { EXTENSIONES_VERIFACTU } from '@/config/extensiones-verifactu'
import UploadFileItem from '@/components/UploadFileItem.vue'

export default
{
  name: 'upload-verifactu-dialog',
  mixins: [UploadDocumentMixin],
  components: {
    Upload,
    UploadFileItem,
  },
  props: {
    dialogOpen: {
      type: Boolean,
      default: false
    },
    mobileVersion: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      WarningType,
      ErrorType,
      isLoadingUpdate: false,
      loading: false,
      isOpen: this.dialogOpen,
      itemWidth: 'fit-content',
      form: {
        ClienteID: '',
        fileMain: [],
        file: []
      },
      currentSchema: '',
      selectedFile: null,
      pgeUploadLimit: 30,
      maxFilesAdj: 5,
      maxSizeFile: 7340032, // Bytes (7MB)
      clientes: [],
      extensionesVerifactu: EXTENSIONES_VERIFACTU,
      rules: {
        ClienteID: [
          {
            required: true,
            message: i18n.t('app.fill_data'),
            trigger: ['blur', 'change'],
          }
        ],
        fileMain: [
          {
            required: true,
            trigger: ['blur', 'change'],
            validator: (rule, value, callback) => {
              if (!this.fileNameMain) {
                callback(new Error(i18n.t('app.fill_data')))
              } else {
                callback()
              }
            }
          },
          {
            required: true,
            trigger: ['blur', 'change'],
            validator: (rule, value, callback) => {
              if (this.fileNameMain) {
                const totalSize = this.form.fileMain.map(x => x.size).reduce((a, b) => a + b, 0)
                if (totalSize > this.maxSizeFile) {
                  callback(new Error(i18n.t('validation.exceed_size_files_total') + ' (' + this.maxMBAdj + ' MB)'))
                } else {
                  callback()
                }
              }
            }
          },
          {
            required: true,
            trigger: ['blur', 'change'],
            validator: (rule, value, callback) => {
              const validExts = ['XML', 'ZIP']
              if (!this.fileNameMain || this.fileNameMain.length < 5 || !this.fileNamesMain.every(name => validExts.includes(this.getExtensionFromFileName(name).toUpperCase()))) {
                callback(new Error(i18n.t('validation.not_valid_extension', { exts: validExts })))
              } else {
                callback()
              }
            }
          },
        ]
      },
      schemasFiles: {
        VERIFACTU: 0,
        NO_VALID: 0,
      },
    }
  },
  watch: {
    // Inicializar al abrir el componente
    
  },
  mounted: async function () {
  },
  computed: {
    SchemaForType: function () {
      return this.tiposEsquema.filter(x => x.types.includes('verifactu')).map(x => x.esquema)
    },
    disableSubmit: function () {
      var disable = Object.values(this.form).filter(x => typeof x !== 'boolean').reduce((a, b) => a || (!b && b !== 0), false)

      disable ||= this.form.fileMain.some(file => file.validations?.some(validation => !validation.fixed) || file.files?.some(efile => efile.validations?.some(validation => !validation.fixed)))
      disable ||= this.form.fileMain.some(file => file.warns?.some(warn => !warn.fixed) || file.files?.some(efile => efile.warns?.some(warn => !warn.fixed)))
      
      return this.isLoadingUpdate || disable
    },
    pgeUploadLimitComputed: function () {
      return this.form.fileMain.some(x => this.getExtensionFromFileName(x.name).toUpperCase() === 'ZIP') ? 1 : this.pgeUploadLimit
    },
    verifactuAcceptFiles: function () {
      const exts = this.fileNamesMain.map(name => this.getExtensionFromFileName(name).toUpperCase())
      if (exts.length === 0) {
        return '.xml,.zip'
      }
      return exts.some(x => ['XML'].includes(x)) ? '.xml' : '.zip'
    },
    maxMBAdj () {
      return (this.maxSizeFile) / 1024 / 1024
    },
    extensionTooltipVerifactuMessage: function () {
      // const exts = this.fileNamesMain.map(name => this.getExtensionFromFileName(name).toUpperCase())
      // const validExts = exts.length === 0 ? [...this.extensionesFacturaePermitidas, 'ZIP'] : (exts.some(x => x === 'ZIP') ? ['ZIP'] : this.extensionesFacturaePermitidas)
      return `${i18n.t('tooltip.total_max_size_files', { maxMB: this.maxMBAdj })} ${i18n.t('validation.extension', { exts: ['XML'] })}`
    },
  },
  methods: {
    getSchemaRequestParams (idCliente) {
      const params = { isReceived: false }
      if ([3, 8].includes(this.role)) params.idCliente = idCliente
      return params
    },
    onFileDrop (event) {
      [...event.dataTransfer.files].filter(file => !file.uid).forEach(file => this.handleChange({ name: file.name, raw: file, status: 'ready' }))
    },
    handleChangeClienteId: async function (ClienteID) {
      if (ClienteID) {
        this.updateSchemaFiles(ClienteID)
      } else {
        this.CifsEmpresaAPS = []
      }
    },
    handleChangeMain: async function (archivo) {
      (this.$refs.fileMainRef || this.$refs.fileReceivedMainRef).clearValidate()
      let fileOk = true
      const exts = this.fileNamesMain.map(name => this.getExtensionFromFileName(name).toUpperCase()).concat(this.getExtensionFromFileName(archivo.name).toUpperCase())
      if (exts.some(x => x === 'ZIP')) {
        if (exts.length > 1) {
          this.doFeedback(i18n.t('validation.onlyonce_zip'))
          this.handleRemoveMain(archivo)
          fileOk = false
        } else {
          await this.checkSchemaZipFile(archivo)
        }
      } else {
        if (!this.extensionesVerifactu.includes(this.getExtensionFromFileName(archivo.name).toUpperCase())) {
          this.doFeedback(i18n.t('validation.not_single_valid_extension', { ext: this.listToCommaString(['.xml', '.zip'], true) }))
          this.handleRemoveMain(archivo)
          fileOk = false
        } else {
          await this.checkSchemaFile(archivo)
          if ((this.form.fileMain.length + 1) === 1 && !archivo.files?.length && !archivo.validations?.length) {
            this.selectFile(archivo)
          }
        }
      }
      
      if (fileOk) {
        this.form.fileMain.push(archivo)
      }
    },
    handleOnExceedMain: function () {
      this.doFeedback(i18n.t('validation.max_files_warn', { limit: this.pgeUploadLimitComputed }), 'warning')
    },
    handleRemoveMain: function (file) {
      this.form.fileMain = this.form.fileMain.filter(x => x.uid !== file.uid)
      const isZipFile = file.linkid === 'ZIPFILE'
      if (file.esquema) {
        if (file.esquema === 'Variado') {
          if (file.files?.length) {
            file.files.forEach(x => { if (x.esquema) { this.schemasFiles[x.esquema] = Math.max(this.schemasFiles[x.esquema] - 1, 0) } })
          }
        } else {
          let n = 1
          let esquema = file.esquema
          if (isZipFile) {
            n = file.files?.length ?? 0
            esquema = file.esquema.slice(0, -1)
          }
          this.schemasFiles[esquema] = Math.max(this.schemasFiles[esquema] - n, 0)
          if (this.schemasFiles[esquema] < 1 && this.currentSchema === esquema) {
            let schema = ''
            this.updateValidationStatus(ErrorType.MISCELANEUS_SCHEMA_ERROR, efile => (schema ||= (efile.esquema && efile.esquema !== 'NO_VALID' ? efile.esquema : '')) === efile.esquema)
            this.currentSchema = schema
          }
        }
      }
      if (this.selectedFile?.linkid === file.linkid && this.selectedFile?.uid === file.uid) {
        this.selectedFile = null
      }
      if (file.files?.length) {
        for (const efile of file.files) {
          if (this.selectedFile?.linkid === efile.linkid && this.selectedFile?.uid === efile.uid) {
            this.selectedFile = null
          }
        }
      }
      this.form.file.filter(x => x.linkedfile.uid === file.uid).forEach(x => this.handleRemove(x))
      const duplicated = this.form.fileMain.filter(x => x.linkid === file.linkid)
      if (duplicated.length) {
        if (duplicated.every(x => x.validations?.some(v => v.type === ErrorType.DUPLICATED_FILE))) {
          this.updateValidationStatus(ErrorType.DUPLICATED_FILE, true, efile => efile.uid === duplicated[0].uid)
        }
      }
    },

    // #region CheckSchema Methods
    checkSchemaFileResponse (archivo, fileData) {
      const fileName = this.initializeSchemaFileResponse(archivo, fileData)

      if (!this.SchemaForType.includes(fileData.esquema)) {
        const msg = `${fileName}: ${i18n.t('feedback.error.not_valid_schema_for_tab_error', { fileType: i18n.t('verifactu.header') })}`
        setTimeout(() => this.doFeedback(msg), 100)
        this.addValidation(archivo, msg, ErrorType.SCHEMA_TYPE_ERROR)
        this.selectFile(archivo, true)
      }
      
      this.registerFileSchema(archivo, fileData)
      
      if (this.validateSchemaFileDetails(archivo, fileData, fileName)) {
        const validateNoMix = (value, selectedValue, message, errorType, target) => {
          if (value && selectedValue !== value) {
            setTimeout(() => this.doFeedback(message), 100)
            this.addValidation(target, message, errorType)
            this.selectFile(target, true)
          }
        }
        this.$set(archivo, 'isSigned', !!fileData.IsSigned)
        this.$set(archivo, 'IdCifEmpresa', fileData.IdCifEmpresaDetected || '')
        this.selectCif ||= archivo.IdCifEmpresa
        validateNoMix(
          archivo.IdCifEmpresa, 
          this.selectCif, 
          i18n.t('feedback.error.miscelaneus_cif_error'), 
          ErrorType.MISCELANEUS_CIF_ERROR, 
          archivo
        )

        const invoices = fileData.FileDetails.Invoices || []
        const hasMultipleInvoices = fileData.FileDetails.InvoicesToImport > 1
        const isMultipleFile = !!(this.tiposEsquema.find(x => x.esquema === fileData.esquema)?.multiple ?? false)
        if (hasMultipleInvoices) {
          this.$set(archivo, 'files', [])
          this.$set(archivo, 'isOpen', false)
        }
          
        for (const invoice of invoices) {
          const innerfile = hasMultipleInvoices ? {} : archivo
          this.$set(innerfile, 'pgeType', invoice.SendTypeSelected || '')
          this.selectPge ||= innerfile.pgeType
          validateNoMix(
            innerfile.pgeType, 
            this.selectPge, 
            i18n.t('feedback.error.miscelaneus_pge_error'), 
            ErrorType.MISCELANEUS_PGE_ERROR, 
            innerfile
          )
          if (isMultipleFile) {
            const idprop = hasMultipleInvoices ? 'linkid' : 'attachid'
            this.$set(innerfile, idprop, `${archivo.linkid}_${invoice.UniqueIdentifierInvoice}`)
            this.$set(innerfile, 'uid', archivo.uid)
            this.$set(innerfile, 'origin', `user.api.invoice.${innerfile.uid}`)
            this.$set(archivo, 'pgeType', archivo.pgeType || invoice.SendTypeSelected || '')
            this.$set(innerfile, 'IdCifEmpresa', fileData.IdCifEmpresaDetected || '')
            if (hasMultipleInvoices) {
              const name = invoice.InvoiceSeries
                ? `${invoice.InvoiceSeries}_${invoice.InvoiceNumber}`
                : `${invoice.InvoiceNumber}`
  
              this.$set(innerfile, 'name', name)
            }
          }
          if (hasMultipleInvoices)archivo.files.push(innerfile)
        }
          
        if (hasMultipleInvoices && archivo.files?.some(x => x.warns?.length)) {
          this.addWarning(archivo, i18n.t('tooltip.issued_any_childfile_warns'), WarningType.MIXED_WARNING, archivo.files.every(x => x.warns?.every(w => w.fixed) ?? true))
        }
      }
      this.setSchemaChildPaths(archivo)
    },
    // #endregion

    resetForm: function () {
      this.resetFormFiles()
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.clearValidate()
      }
    },
    initialiceData: async function () {
      this.resetAllForm()
      if ([8, 3].includes(this.role)) {
        const dataClients = await getClients()
        if (dataClients.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', dataClients))
        }
        this.clientes = dataClients.Clientes.map(x => ({ label: x.Nombre, value: x.idCliente }))
        this.form.ClienteID = this.clientFilter || ''
      } else {
        this.form.ClienteID = this.getIdEmpresaLogged || ''
      }
      this.selectedFile = null
    },
    submitForm: async function (ruleForm) {
      const noWarnings = !this.warningError || (this.isWarningFixed(this.form.fileMain) && this.isWarningFixed(this.form.file))
      const noValidations = !this.validationError || (this.isValidationFixed(this.form.fileMain) && this.isValidationFixed(this.form.file))
      if (noValidations && noWarnings) {
        this.uploadVerifactu(ruleForm)
      } else if (!noWarnings) {
        this.doFeedback(i18n.t('feedback.error.process_data_error'), 'warning')
      } else {
        this.doFeedback(i18n.t('feedback.error.process_data_error'))
      }
    },
    uploadVerifactu: function (ruleForm) {
      try {
        this.$store.commit('setIsLoading', true)
        this.$refs[ruleForm].validate(async valid => {
          if (!valid) {
            this.doFeedback(i18n.t('feedback.error.process_data_error'))
            return
          }
          this.isLoadingUpdate = true

          const files = this.form.fileMain.concat(this.form.file)
          const params = {
            multiSend: !!this.multiSend,
            idCliente: 0,
          }
          if ([3, 8].includes(this.role)) {
            params.idCliente = this.form.ClienteID
          }
          if (this.multiSend) {
            altaVerifactuPortal(files, params).then(response => {
              if (response.blnError === true) {
                this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response), response.codError === 'FCSF012' ? 'warning' : 'error')
              } else {
                this.doFeedback(i18n.tc('verifactu.verifactu_success', 1), 'success')
                setTimeout(() => {
                  this.toggleDialog()
                }, 100)
              }
              this.$emit('applyDialog', { results: response.Respuestas })
            }).catch(() => {
              this.doFeedback(i18n.t('feedback.error.internal_error_message'))
              this.$emit('applyDialog', {})
            }).finally(() => { this.isLoadingUpdate = false })
            this.resetForm()
            this.resetSchemasFiles()
            this.$emit('applyDialog', { background: true })
          } else {
            const response = await altaVerifactuPortal(files, params)
            if (response.blnError === true) {
              this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
            } else {
              this.resetForm()
              this.resetSchemasFiles()
              this.$emit('applyDialog', {})
              setTimeout(() => {
                this.toggleDialog()
              }, 100)
              this.doFeedback(i18n.tc('verifactu.verifactu_success', 1), 'success')
            }
            this.isLoadingUpdate = false
          }
        })
      } catch (error) {
        this.isLoadingUpdate = false
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
#fileMainSelector::v-deep .el-upload-list {
  max-height: 67px !important;
}

::v-deep .el-upload-list__item:first-child {
  margin-top: 5px;
}

.upload-file-list {
  padding-bottom: 0;
}

.el-form-item {
  margin-bottom: 0;
}
</style>
